function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { readableColor, rgba } from 'polished';
import React from 'react';
import styled, { css } from 'styled-components';
import SpinnerThin from '../icons/solid/SpinnerThin';
import { animated } from '../lib/hoc/withAnimation';
import { withSpacing } from '../lib/hoc/withSpacing';
import animations from '../styles/animations';
import { margin, shade, themify, tint } from '../styles/mixins';
import { curves, type } from '../styles/theme';
import { Icon } from './Icon'; // Try to use react-router-dom's Link, or fall back to Next's Link of that fails

let ReactRouterLink;
let NextLink;
/** Whether the Link element is a react-router-dom or a next/link Link */

let IS_NEXT_LINK = false;

try {
  // TODO: this will print a warning even though the error is caught
  // already tried doing `await import('react-router-dom'): had to set experiments.topLevelAwait = true in chili's webpack config: note allowed. "Module parse failed: Top-level-await is only supported in EcmaScript Modules"
  // that should have resolved it (or would have allowed us to add a /* webpackIgnore: true */ log)
  const NextLinkRoot = require('next/link');

  NextLink = NextLinkRoot.default;
  IS_NEXT_LINK = true;
} catch (er) {
  try {
    const RRD = require('react-router-dom');

    ReactRouterLink = RRD.Link;
  } catch (_unused) {
    console.error('No Link component could be determined: neither `react-router-dom` nor `next` can be imported as optional peer dependency.');
  }
}

const determineButtonColor = ({
  $bgColor,
  $color,
  $inverse,
  $minimal
}, buttonState) => {
  if (!$color) return readableColor($bgColor);

  if (buttonState === 'hover') {
    if ($minimal) return $bgColor;
    if ($inverse) return $color;
    return 'white';
  }

  if ($inverse || $minimal) return $bgColor;
  return $color;
};

const determineButtonBgColor = ({
  $bgColor,
  $inverse,
  $minimal
}, buttonState) => {
  if (buttonState === 'hover') {
    if ($minimal) return rgba($bgColor, 0.1);
    if ($inverse) return $bgColor;
    return shade(0.9, $bgColor);
  }

  if ($inverse || $minimal) return 'transparent';
  return $bgColor;
};

const ButtonIcon = styled(Icon).withConfig({
  displayName: "Button__ButtonIcon",
  componentId: "gb302v-0"
})(["position:relative;vertical-align:top;z-index:3;min-height:", ";min-width:", ";", ";", ";", ";"], x => x.$minIconSize, x => x.$minIconSize, x => x.$placement === 'start' && margin(2, 'right'), x => x.$placement === 'end' && margin(2, 'left'), x => x.$iconAnimation && css(["display:block;animation:", " 2s infinite;animation-timing-function:linear;"], x => x.$iconAnimation ? animations[x.$iconAnimation] : ''));
const ButtonLabel = styled.span.withConfig({
  displayName: "Button__ButtonLabel",
  componentId: "gb302v-1"
})(["align-self:auto;z-index:3;"]);
const activeStyles = css(["background:", ";border-color:", ";color:", ";box-shadow:", ";", "{q & *{fill:", " !important;}}", "{text-shadow:", ";}"], x => x.$minimal ? `${rgba(x.$bgColor, 0.2)}` : shade(0.85, x.$bgColor), x => x.$minimal ? 'transparent' : shade(0.8, x.$borderColor || x.$bgColor), x => x.$minimal ? x.$bgColor : x.$inverse ? x.$color : 'white', x => x.$minimal ? 'none' : `inset 0 0 3px 3px ${shade(0.8, x.$bgColor)}`, ButtonIcon, x => x.$minimal ? x.$bgColor : x.$inverse ? x.$color : 'white', ButtonLabel, x => x.$minimal ? 'none' : `1px 1px 3px ${shade(0.8, x.$bgColor)}`);
const disabledStyles = css(["opacity:0.75;filter:saturate(0.75) brightness(0.95);cursor:not-allowed;"]);
const StyledButton = styled.button.withConfig({
  displayName: "Button__StyledButton",
  componentId: "gb302v-2"
})(["appearance:none;position:relative;display:", ";width:", ";align-items:center;justify-content:", ";text-align:", ";min-height:3em;vertical-align:middle;background:", ";color:", ";border-width:1px;border-style:solid;border-color:", ";border-radius:", ";font-size:", ";font-weight:600;line-height:1;text-transform:", ";cursor:", ";z-index:1;&:disabled{", "}", " ", ";", ";&:link{color:", ";&:hover{color:", ";}}"], x => x.$fluid ? 'flex' : 'inline-flex', x => x.$fluid ? '100%' : 'auto', x => x.$justifyContent, x => x.$textAlign, x => determineButtonBgColor(x), x => determineButtonColor(x), x => x.$borderColor, x => x.$round ? '999px' : '6px', x => type.scale[x.$fontSize], x => x.$upperCase ? 'uppercase' : 'none', x => x.$loading ? 'progress' : 'pointer', disabledStyles, x => Boolean(x.$error) && css(["animation:", ";animation-iteration-count:1;animation-duration:1s;"], animations.shake), x => x.$loading && css(["pointer-events:none;&::after{position:absolute;content:'';z-index:2;top:0;right:0;bottom:0;left:0;border-radius:3px;animation-fill-mode:forwards;animation-iteration-count:1;", ";}"], x => x.$loaded ? css(["animation:", " 0.25s !important;animation-timing-function:", ";"], animations.finishProgress, curves.accelerate) : css(["background:", ";animation:", " ", "s;animation-timing-function:", ";"], x => tint(0.75, x.$bgColor), animations.fauxProgress, x => x.$loadTime, curves.decelerate)), x => !x.disabled && css(["&:hover,&:focus{background:", ";border-color:", ";color:", ";", "{& *{fill:", " !important;}}}", " &:active{", "}"], x => determineButtonBgColor(x, 'hover'), x => x.$minimal ? 'transparent' : shade(0.9, x.$borderColor || x.$bgColor), x => determineButtonColor(x, 'hover'), ButtonIcon, x => x.$minimal ? x.$bgColor : x.$inverse ? x.$color : 'white', x => x.$isActive && css(["", ""], activeStyles), activeStyles), x => x.$inverse || x.$minimal ? x.$bgColor : x.$color, x => x.$minimal ? x.$bgColor : x.$color);
export const ButtonFunc = (_ref) => {
  let {
    bgColor = 'green',
    children,
    color = 'white',
    disabled,
    error,
    fluid,
    fontSize = 5,
    href,
    iconAnimation,
    iconEnd,
    iconStart,
    inverse,
    isActive,
    justifyContent = 'center',
    label,
    loaded,
    loading = false,
    loadTime = 1,
    minIconSize = '1em',
    minimal,
    onClick,
    round,
    target,
    textAlign = 'center',
    title,
    to,
    upperCase = false
  } = _ref,
      rest = _objectWithoutProperties(_ref, ["bgColor", "children", "color", "disabled", "error", "fluid", "fontSize", "href", "iconAnimation", "iconEnd", "iconStart", "inverse", "isActive", "justifyContent", "label", "loaded", "loading", "loadTime", "minIconSize", "minimal", "onClick", "round", "target", "textAlign", "title", "to", "upperCase"]);

  const getIconColor = () => {
    let iconColor;
    if (inverse || minimal) return iconColor = bgColor;
    if (bgColor === 'white') return color;
    iconColor = color;
    return themify(iconColor);
  };

  const getBorderColor = () => {
    let borderColor;
    if (minimal) return borderColor = 'transparent';
    if (inverse) return borderColor = themify(bgColor);
    if (error) return borderColor = themify('redDark');
    borderColor = shade(0.95, themify(bgColor));
    return borderColor;
  };

  const IconStart = iconStart && /*#__PURE__*/React.createElement(ButtonIcon, {
    $iconAnimation: loading && !loaded ? 'spin' : iconAnimation,
    $minIconSize: minIconSize,
    $placement: "start",
    fill: getIconColor(),
    icon: loading && !loaded ? SpinnerThin : iconStart,
    inline: true,
    solid: true,
    width: fontSize ? type.scale[fontSize] : undefined
  });
  const IconEnd = iconEnd && /*#__PURE__*/React.createElement(ButtonIcon, {
    $iconAnimation: loading && !loaded ? 'spin' : iconAnimation,
    $minIconSize: minIconSize,
    $placement: "end",
    fill: getIconColor(),
    icon: loading && !loaded ? SpinnerThin : iconEnd,
    inline: true,
    solid: true,
    width: fontSize ? type.scale[fontSize] : undefined
  });

  const buttonProps = _objectSpread({
    'aria-label': rest.ariaLabel || label,
    $bgColor: themify(error ? 'red' : bgColor),
    $borderColor: getBorderColor(),
    $color: themify(error ? 'white' : color),
    $error: error,
    $fluid: fluid,
    $fontSize: fontSize,
    $inverse: inverse,
    $isActive: isActive,
    $justifyContent: justifyContent,
    $loaded: loaded,
    $loading: loading && !loaded,
    $loadTime: loadTime,
    $minimal: minimal,
    $round: round,
    $textAlign: textAlign,
    $upperCase: upperCase,
    disabled,
    onClick: disabled || loading && !loaded ? undefined : onClick,
    title: title || (typeof children === 'string' ? href ? `Ga naar ${href}` : children : '')
  }, rest); // For NextJS links, the Link should only have href and needs to be a wrapper around <a />


  const ToButton = /*#__PURE__*/React.createElement(StyledButton, _extends({}, buttonProps, {
    as: IS_NEXT_LINK ? 'a' : ReactRouterLink,
    to: IS_NEXT_LINK ? undefined : to
  }), IconStart, /*#__PURE__*/React.createElement(ButtonLabel, null, error || label || children), IconEnd);
  return !disabled && to ? IS_NEXT_LINK ? /*#__PURE__*/React.createElement(NextLink, {
    href: to,
    passHref: true
  }, ToButton) : ToButton : !disabled && href ? /*#__PURE__*/React.createElement(StyledButton, _extends({
    as: "a"
  }, buttonProps, {
    href: href,
    target: href && !target ? '_blank' : target
  }), IconStart, /*#__PURE__*/React.createElement(ButtonLabel, null, error || label || children), IconEnd) : /*#__PURE__*/React.createElement(StyledButton, buttonProps, IconStart, /*#__PURE__*/React.createElement(ButtonLabel, null, error || label || children), IconEnd);
};
export const Button = animated(withSpacing(ButtonFunc, {
  p: 2,
  mb: 1,
  mr: 1
}));